/* Complimentary.jsx - Ödenmez (İkram) Modülü */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, FileText, Wine, Coffee, UtensilsCrossed, Package, Save, Users, ShoppingBag, Calendar, ClipboardList, BarChart3, Pencil, Check, X as XIcon } from 'lucide-react';
import { formatTRNumber } from '../lib/utils';

const BAR_OPTIONS = [
    { value: 'lobby', label: 'Lobby Bar' },
    { value: 'servis', label: 'Servis Bar' },
    { value: 'vitamin', label: 'Vitamin Bar' },
    { value: 'havuz', label: 'Havuz Bar' },
    { value: 'plaj', label: 'Plaj Bar' },
    { value: 'amfi', label: 'Amfi Bar' },
    { value: 'teras', label: 'Teras Bar' },
    { value: 'alacarte', label: 'Alacarte Bar' },
    { value: 'minibar', label: 'Minibar' },
];
const BAR_NAME_BY_ID = Object.fromEntries(BAR_OPTIONS.map(b => [b.value, b.label]));

const CATEGORIES = [
    { value: 'alkollu', label: 'Alkollü', icon: Wine, color: 'bg-rose-100 text-rose-700 border-rose-200', activeColor: 'bg-rose-600 text-white' },
    { value: 'soft', label: 'Soft İçecek', icon: Coffee, color: 'bg-sky-100 text-sky-700 border-sky-200', activeColor: 'bg-sky-600 text-white' },
    { value: 'yemek', label: 'Yemek', icon: UtensilsCrossed, color: 'bg-amber-100 text-amber-700 border-amber-200', activeColor: 'bg-amber-600 text-white' },
    { value: 'diger', label: 'Diğer', icon: Package, color: 'bg-slate-100 text-slate-700 border-slate-200', activeColor: 'bg-slate-700 text-white' },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));

export default function Complimentary() {
    const navigate = useNavigate();
    const today = new Date().toISOString().slice(0, 10);

    // Records list & filters — TEK TARİH (talimat: date_from = date_to = seçili gün)
    const [records, setRecords] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [filterDate, setFilterDate] = useState('');  // '' = tüm tarihler
    const [filterBar, setFilterBar] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [reportType, setReportType] = useState('daily');  // 'daily' | 'monthly'

    // KAYIT sekmesi — seçili bar + tarih için basit liste (üstteki form alanlarını kullanır)
    const [barRecords, setBarRecords] = useState([]);

    // New record form
    const [recordDate, setRecordDate] = useState(today);
    const [recordBar, setRecordBar] = useState('lobby');
    const [personSearch, setPersonSearch] = useState('');
    const [personnelOptions, setPersonnelOptions] = useState([]);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [activeCategory, setActiveCategory] = useState('alkollu');
    const [productSearch, setProductSearch] = useState('');
    const [productOptions, setProductOptions] = useState([]);
    const [cart, setCart] = useState([]);  // {product_id, product_name, category, quantity, unit_price_eur}
    const [saving, setSaving] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);  // bar tablosunu zorla yenilemek için

    // KAYIT sekmesi: seçili bar + tarih değişince ilgili kayıtları çek
    useEffect(() => {
        const fetchBarRecords = async () => {
            try {
                const res = await api.get('/complimentary', {
                    params: { date_from: recordDate, date_to: recordDate, bar_id: recordBar },
                });
                setBarRecords(res.data || []);
            } catch {
                setBarRecords([]);
            }
        };
        if (recordDate && recordBar) fetchBarRecords();
    }, [recordDate, recordBar, saving, refreshTick]);

    useEffect(() => {
        fetchRecords();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Personnel autocomplete (debounced)
    useEffect(() => {
        const t = setTimeout(async () => {
            try {
                const res = await api.get('/complimentary/personnel', { params: personSearch ? { q: personSearch } : {} });
                setPersonnelOptions(res.data || []);
            } catch { /* ignore */ }
        }, 250);
        return () => clearTimeout(t);
    }, [personSearch]);

    // Product autocomplete (debounced, filtered by category)
    // KURAL: arama metni boşsa fetch ATMA + listeyi boşalt (otomatik açılma yok).
    useEffect(() => {
        const term = (productSearch || '').trim();
        if (!term) {
            setProductOptions([]);
            return;
        }
        const t = setTimeout(async () => {
            try {
                // Tüm katalogdan ara — kategori filtresi YOK (kullanıcı yiyecek/içecek/alkol fark etmeksizin tümünü görsün)
                const res = await api.get('/complimentary/products', {
                    params: { q: term },
                });
                setProductOptions(res.data || []);
            } catch { /* ignore */ }
        }, 250);
        return () => clearTimeout(t);
    }, [productSearch, activeCategory]);

    const fetchRecords = async () => {
        setLoadingList(true);
        try {
            const params = {};
            // Tek tarih → date_from = date_to = filterDate
            if (filterDate) {
                params.date_from = filterDate;
                params.date_to = filterDate;
            }
            if (filterBar !== 'all') params.bar_id = filterBar;
            if (filterCategory !== 'all') params.category = filterCategory;
            const res = await api.get('/complimentary', { params });
            setRecords(res.data || []);
        } catch {
            toast.error('Kayıtlar yüklenemedi');
        } finally {
            setLoadingList(false);
        }
    };

    // filterDate değişince otomatik yeniden çek
    useEffect(() => {
        fetchRecords();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterDate]);

    const addToCart = (product) => {
        setCart(prev => {
            const existing = prev.find(c => c.product_id === product.id && c.category === product.category);
            if (existing) {
                return prev.map(c => c === existing ? { ...c, quantity: c.quantity + 1 } : c);
            }
            return [...prev, {
                product_id: product.id,
                product_name: product.name,
                category: product.category,
                quantity: 1,
                unit_price_eur: product.unit_price_eur || 0,
            }];
        });
        setProductSearch('');
    };

    const updateCartItem = (idx, field, value) => {
        setCart(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
    };

    const removeFromCart = (idx) => {
        setCart(prev => prev.filter((_, i) => i !== idx));
    };

    const cartTotalEur = cart.reduce((s, c) => s + (parseFloat(c.quantity) || 0) * (parseFloat(c.unit_price_eur) || 0), 0);

    const resetForm = () => {
        setCart([]);
        setSelectedPerson(null);
        setPersonSearch('');
        setProductSearch('');
    };

    const saveRecord = async () => {
        if (!selectedPerson) {
            toast.error('Personel seçilmedi');
            return;
        }
        if (cart.length === 0) {
            toast.error('En az bir ürün ekleyin');
            return;
        }
        // Cart'ta fiyatı 0 olan ürün varsa engelle — kullanıcı yanlışlıkla fiyat girmemiş demektir
        const zeroPriced = cart.filter(c => Number(c.unit_price_eur || 0) <= 0);
        if (zeroPriced.length > 0) {
            toast.error(`Şu ürün(ler)in Birim € fiyatı 0: ${zeroPriced.map(c => c.product_name).join(', ')}. Lütfen sepette fiyatları doldurun.`);
            return;
        }
        setSaving(true);
        try {
            await api.post('/complimentary/batch', {
                date: recordDate,
                bar_id: recordBar,
                full_name: selectedPerson.full_name,
                title: selectedPerson.title || '',
                department: selectedPerson.department || '',
                items: cart.map(c => ({
                    product_id: c.product_id,
                    product_name: c.product_name,
                    category: c.category,
                    quantity: parseFloat(c.quantity) || 1,
                    unit_price_eur: parseFloat(c.unit_price_eur) || 0,
                })),
            });
            toast.success(`${cart.length} kayıt eklendi`);
            resetForm();
            fetchRecords();
        } catch (e) {
            const det = e?.response?.data?.detail;
            const msg = typeof det === 'string' ? det : (Array.isArray(det) ? det.map(x => x?.msg || JSON.stringify(x)).join(', ') : 'Kayıt başarısız');
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/complimentary/${id}`);
            toast.success('Silindi');
            fetchRecords();
            setRefreshTick(t => t + 1);
        } catch {
            toast.error('Silme başarısız');
        }
    };

    // Inline düzenleme state
    const [editingId, setEditingId] = useState(null);
    const [editQty, setEditQty] = useState(0);
    const [editPrice, setEditPrice] = useState(0);

    const startEdit = (r) => {
        setEditingId(r.id);
        setEditQty(r.quantity || 0);
        setEditPrice(r.unit_price_eur || 0);
    };
    const cancelEdit = () => setEditingId(null);
    const saveEdit = async (id) => {
        try {
            await api.put(`/complimentary/${id}`, {
                quantity: Number(editQty) || 0,
                unit_price_eur: Number(editPrice) || 0,
            });
            toast.success('Güncellendi');
            setEditingId(null);
            fetchRecords();
            setRefreshTick(t => t + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Güncelleme başarısız');
        }
    };

    const filteredRecords = useMemo(() => records.filter(r => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (r.full_name || '').toLowerCase().includes(s) ||
            (r.product_name || '').toLowerCase().includes(s) ||
            (r.department || '').toLowerCase().includes(s);
    }), [records, searchTerm]);

    const totalEur = filteredRecords.reduce((s, r) => s + (r.total_eur || 0), 0);
    const totalTl = filteredRecords.reduce((s, r) => s + (r.total_tl || 0), 0);

    const setDateQuick = (kind) => {
        const today = new Date().toISOString().slice(0, 10);
        if (kind === 'today') {
            setFilterDate(today);
        } else if (kind === 'all') {
            setFilterDate('');
        }
    };

    const downloadPdf = () => {
        const params = new URLSearchParams();
        // Seçili gün YOKSA bugünü kullan (rapor mantıklı bir tarih bekler)
        const baseDate = filterDate || new Date().toISOString().slice(0, 10);
        if (reportType === 'monthly') {
            // Ayın 1'i → son günü
            const d = new Date(baseDate + 'T00:00:00');
            const y = d.getFullYear();
            const m = d.getMonth();
            const first = new Date(y, m, 1).toISOString().slice(0, 10);
            const last = new Date(y, m + 1, 0).toISOString().slice(0, 10);
            params.set('date_from', first);
            params.set('date_to', last);
            params.set('report_type', 'monthly');
        } else {
            params.set('date_from', baseDate);
            params.set('date_to', baseDate);
            params.set('report_type', 'daily');
        }
        const token = localStorage.getItem('token');
        if (token) params.set('token', token);
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/complimentary/export/pdf?${params.toString()}`, '_blank');
    };

    return (
        <div className="space-y-4" data-testid="complimentary-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="border-slate-300 hover:bg-slate-100" data-testid="back-btn">
                        ← Geri
                    </Button>
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-slate-800">Ödenmez Takip</h1>
                        <p className="text-sm text-slate-500">İdari personel ödenmez kayıtları — birim fiyat EUR</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate('/odenmez/personnel')} data-testid="manage-personnel-btn">
                        <Users className="w-4 h-4 mr-2" /> Personel
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/odenmez/products')} data-testid="manage-products-btn">
                        <ShoppingBag className="w-4 h-4 mr-2" /> Ürünler
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="kayit">
                <TabsList data-testid="comp-tabs">
                    <TabsTrigger value="kayit" data-testid="tab-kayit">
                        <ClipboardList className="w-4 h-4 mr-1" /> Kayıt
                    </TabsTrigger>
                    <TabsTrigger value="raporlar" data-testid="tab-raporlar">
                        <BarChart3 className="w-4 h-4 mr-1" /> Raporlar
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="kayit" className="space-y-4 mt-4">
            {/* New Record Card */}
            <Card className="border-2 border-emerald-100 bg-emerald-50/30">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                    <CardTitle className="text-base">
                        Yeni Ödenmez Kaydı
                    </CardTitle>
                    <Button onClick={saveRecord} disabled={saving || !selectedPerson || cart.length === 0} className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="save-record-btn">
                        <Save className="w-4 h-4 mr-2" /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Top row: date + bar + person */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                            <Label className="text-xs">Tarih</Label>
                            <Input type="date" value={recordDate} onChange={(e) => setRecordDate(e.target.value)} className="h-10" data-testid="record-date" />
                        </div>
                        <div>
                            <Label className="text-xs">Bar</Label>
                            <Select value={recordBar} onValueChange={setRecordBar}>
                                <SelectTrigger className="h-10" data-testid="record-bar"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {BAR_OPTIONS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="md:col-span-2 relative">
                            <Label className="text-xs">Personel (ad ile ara)</Label>
                            <Input
                                value={personSearch}
                                onChange={(e) => { setPersonSearch(e.target.value); setSelectedPerson(null); }}
                                placeholder="Örn: Abdullah"
                                className="h-10"
                                data-testid="person-search"
                            />
                            {personSearch && !selectedPerson && personnelOptions.length > 0 && (
                                <div className="absolute z-10 left-0 right-0 mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-56 overflow-y-auto">
                                    {personnelOptions.slice(0, 12).map(p => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => { setSelectedPerson(p); setPersonSearch(p.full_name); }}
                                            className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b last:border-0"
                                            data-testid={`person-option-${p.id}`}
                                        >
                                            <div className="font-medium text-sm">{p.full_name}</div>
                                            <div className="text-xs text-slate-500">{p.title || '—'} · {p.department || '—'}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {selectedPerson && (
                                <div className="mt-1 inline-flex items-center gap-2 bg-emerald-100 border border-emerald-200 text-emerald-800 rounded-full px-3 py-1 text-xs">
                                    <span className="font-semibold">{selectedPerson.full_name}</span>
                                    <span className="opacity-70">· {selectedPerson.title || '—'} · {selectedPerson.department || '—'}</span>
                                    <button onClick={() => { setSelectedPerson(null); setPersonSearch(''); }} className="text-emerald-700 hover:text-emerald-900">×</button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Ürün arama (kategori filtresiz, tüm ürünlerde arar) */}
                    <div>
                        <Label className="text-xs">Ürün ara</Label>
                        <div className="relative mt-1.5">
                            <Input
                                value={productSearch}
                                onChange={(e) => setProductSearch(e.target.value)}
                                placeholder="Ürün adı yazın..."
                                className="h-10"
                                data-testid="product-search"
                            />
                            {productSearch.trim() && productOptions.length > 0 && (
                                <div className="absolute z-10 left-0 right-0 mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-60 overflow-y-auto">
                                    {productOptions.slice(0, 20).map(p => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => addToCart(p)}
                                            className="w-full text-left px-3 py-2 hover:bg-sky-50 border-b last:border-0 flex justify-between items-center"
                                            data-testid={`product-option-${p.id}`}
                                        >
                                            <span className="font-medium text-sm">{p.name}</span>
                                            <span className="text-xs text-slate-500">{formatTRNumber(p.unit_price_eur, 2)} €</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {productSearch.trim() && productOptions.length === 0 && (
                                <div className="absolute z-10 left-0 right-0 mt-1 bg-white rounded-lg border border-amber-200 px-3 py-2 text-xs text-amber-700 shadow">
                                    "{productSearch}" bulunamadı. Önce "Ürünler" sekmesinden ekleyin.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Cart */}
                    {cart.length > 0 && (
                        <div className="border rounded-lg bg-white">
                            <div className="px-4 py-2 bg-slate-50 border-b flex items-center justify-between">
                                <span className="text-sm font-semibold text-slate-700">Sepet ({cart.length})</span>
                                <span className="text-sm font-bold text-emerald-700">Toplam: {formatTRNumber(cartTotalEur, 2)} €</span>
                            </div>
                            <div className="divide-y">
                                {cart.map((c, idx) => {
                                    const catObj = CATEGORIES.find(x => x.value === c.category);
                                    const itemTotal = (parseFloat(c.quantity) || 0) * (parseFloat(c.unit_price_eur) || 0);
                                    return (
                                        <div key={idx} className="flex items-center gap-2 px-3 py-2" data-testid={`cart-item-${idx}`}>
                                            <Badge variant="outline" className={catObj ? catObj.color : ''}>{catObj?.label || c.category}</Badge>
                                            <span className="flex-1 text-sm font-medium">{c.product_name}</span>
                                            <Input type="number" step="0.5" min="0" value={c.quantity} onChange={(e) => updateCartItem(idx, 'quantity', e.target.value)} className="w-20 h-8 text-center" />
                                            <Input type="number" step="0.01" min="0" value={c.unit_price_eur} onChange={(e) => updateCartItem(idx, 'unit_price_eur', e.target.value)} className="w-24 h-8 text-right" />
                                            <span className="text-xs text-slate-500">€</span>
                                            <span className="w-24 text-right font-semibold text-sm">{formatTRNumber(itemTotal, 2)} €</span>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeFromCart(idx)} data-testid={`cart-remove-${idx}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                </CardContent>
            </Card>

            {/* KAYIT sekmesi — seçili bar + tarih için kayıtlar (salt görüntü) */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
                        <span>{BAR_OPTIONS.find(b => b.value === recordBar)?.label || 'Bar'} — {recordDate}</span>
                        <Badge variant="outline" data-testid="bar-record-count">{barRecords.length} kayıt</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    {barRecords.length === 0 ? (
                        <div className="text-center py-6 text-slate-400 text-sm">
                            Bu bar ve tarihte ödenmez kaydı yok
                        </div>
                    ) : (
                        <div className="overflow-x-auto border rounded-lg" data-testid="bar-records-table">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-100">
                                    <tr>
                                        <th className="text-left p-2 font-medium text-slate-600">Personel</th>
                                        <th className="text-left p-2 font-medium text-slate-600">Departman</th>
                                        <th className="text-left p-2 font-medium text-slate-600">Ürün</th>
                                        <th className="text-right p-2 font-medium text-slate-600">Adet</th>
                                        <th className="text-right p-2 font-medium text-slate-600">Birim €</th>
                                        <th className="text-right p-2 font-medium text-slate-600">Toplam €</th>
                                        <th className="text-center p-2 font-medium text-slate-600 w-28">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {barRecords.map((r, i) => {
                                        const isEditing = editingId === r.id;
                                        const liveTotal = (Number(editQty) || 0) * (Number(editPrice) || 0);
                                        return (
                                            <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`bar-record-${r.id}`}>
                                                <td className="p-2 font-medium">{r.full_name}</td>
                                                <td className="p-2 text-slate-600">{r.department || '-'}</td>
                                                <td className="p-2">{r.product_name}</td>
                                                <td className="p-2 text-right">
                                                    {isEditing ? (
                                                        <Input type="number" min="0" step="0.01" value={editQty} onChange={(e) => setEditQty(e.target.value)} className="h-7 w-20 text-right inline-block" data-testid={`bar-edit-qty-${r.id}`} />
                                                    ) : formatTRNumber(r.quantity, 2)}
                                                </td>
                                                <td className="p-2 text-right">
                                                    {isEditing ? (
                                                        <Input type="number" min="0" step="0.01" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="h-7 w-20 text-right inline-block" data-testid={`bar-edit-price-${r.id}`} />
                                                    ) : formatTRNumber(r.unit_price_eur, 2)}
                                                </td>
                                                <td className="p-2 text-right font-semibold text-emerald-700">
                                                    {formatTRNumber(isEditing ? liveTotal : r.total_eur, 2)} €
                                                </td>
                                                <td className="p-2 text-center whitespace-nowrap">
                                                    {isEditing ? (
                                                        <>
                                                            <Button variant="default" size="sm" onClick={() => saveEdit(r.id)} className="bg-emerald-600 hover:bg-emerald-700 h-8 mr-1" data-testid={`bar-save-edit-${r.id}`}>
                                                                <Check className="w-4 h-4 mr-1" /> Kaydet
                                                            </Button>
                                                            <Button variant="outline" size="sm" onClick={cancelEdit} className="h-8" data-testid={`bar-cancel-edit-${r.id}`}>
                                                                <XIcon className="w-4 h-4" />
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Button variant="outline" size="sm" onClick={() => startEdit(r)} className="border-blue-500 text-blue-600 hover:bg-blue-50 h-8 mr-1" data-testid={`bar-edit-${r.id}`}>
                                                                <Pencil className="w-4 h-4 mr-1" /> Düzenle
                                                            </Button>
                                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="text-red-600 h-8" data-testid={`bar-delete-${r.id}`}>
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="bg-emerald-50 border-t-2 border-emerald-300">
                                    <tr>
                                        <td colSpan={5} className="p-2 font-bold text-right">TOPLAM</td>
                                        <td className="p-2 text-right font-bold text-emerald-700">
                                            {formatTRNumber(barRecords.reduce((s, r) => s + (r.total_eur || 0), 0), 2)} €
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
                </TabsContent>

                <TabsContent value="raporlar" className="mt-4">
            {/* Filter & Records */}
            <Card>
                <CardContent className="pt-4 space-y-3">
                    <div className="flex flex-wrap items-end gap-2">
                        <div>
                            <Label className="text-xs">Tarih</Label>
                            <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="h-9 w-40" data-testid="filter-date" />
                        </div>
                        <div><Label className="text-xs">Bar</Label>
                            <Select value={filterBar} onValueChange={setFilterBar}>
                                <SelectTrigger className="h-9 w-40" data-testid="filter-bar"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tüm Barlar</SelectItem>
                                    {BAR_OPTIONS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label className="text-xs">Kategori</Label>
                            <Select value={filterCategory} onValueChange={setFilterCategory}>
                                <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tümü</SelectItem>
                                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1 min-w-[160px]">
                            <Label className="text-xs">Ara</Label>
                            <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Kişi/ürün ara..." className="h-9" />
                        </div>
                        <Button variant="outline" onClick={fetchRecords} className="h-9" data-testid="apply-filter-btn">Filtrele</Button>
                        <div>
                            <Label className="text-xs">Rapor tipi</Label>
                            <Select value={reportType} onValueChange={setReportType}>
                                <SelectTrigger className="h-9 w-32" data-testid="report-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="daily">Günlük</SelectItem>
                                    <SelectItem value="monthly">Aylık</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button variant="outline" onClick={downloadPdf} className="h-9 border-red-300 text-red-700 hover:bg-red-50" data-testid="download-pdf-btn">
                            <FileText className="w-4 h-4 mr-1" /> PDF
                        </Button>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-100 border-b">
                                <tr>
                                    <th className="text-left p-2 font-medium text-slate-600">Tarih</th>
                                    <th className="text-left p-2 font-medium text-slate-600">Personel</th>
                                    <th className="text-left p-2 font-medium text-slate-600">Departman</th>
                                    <th className="text-left p-2 font-medium text-slate-600">Bar</th>
                                    <th className="text-left p-2 font-medium text-slate-600">Kategori</th>
                                    <th className="text-left p-2 font-medium text-slate-600">Ürün</th>
                                    <th className="text-right p-2 font-medium text-slate-600">Adet</th>
                                    <th className="text-right p-2 font-medium text-slate-600">Birim €</th>
                                    <th className="text-right p-2 font-medium text-slate-600">Toplam €</th>
                                    <th className="p-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingList ? (
                                    <tr><td colSpan="10" className="p-8 text-center text-slate-400">Yükleniyor...</td></tr>
                                ) : filteredRecords.length === 0 ? (
                                    <tr><td colSpan="10" className="p-8 text-center text-slate-400">Kayıt yok</td></tr>
                                ) : filteredRecords.map(r => {
                                    const cat = CATEGORIES.find(x => x.value === r.category);
                                    return (
                                        <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`record-${r.id}`}>
                                            <td className="p-2">{r.date}</td>
                                            <td className="p-2 font-medium">{r.full_name}</td>
                                            <td className="p-2 text-slate-600">{r.department || '-'}</td>
                                            <td className="p-2">{BAR_NAME_BY_ID[r.bar_id] || r.bar_id}</td>
                                            <td className="p-2"><Badge variant="outline" className={cat ? cat.color : ''}>{cat?.label || r.category}</Badge></td>
                                            <td className="p-2">{r.product_name}</td>
                                            <td className="p-2 text-right">
                                                {editingId === r.id ? (
                                                    <Input type="number" min="0" step="0.01" value={editQty} onChange={(e) => setEditQty(e.target.value)} className="h-7 w-20 text-right inline-block" data-testid={`edit-qty-${r.id}`} />
                                                ) : formatTRNumber(r.quantity || 0, 2)}
                                            </td>
                                            <td className="p-2 text-right">
                                                {editingId === r.id ? (
                                                    <Input type="number" min="0" step="0.01" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="h-7 w-20 text-right inline-block" data-testid={`edit-price-${r.id}`} />
                                                ) : formatTRNumber(r.unit_price_eur || 0, 2)}
                                            </td>
                                            <td className="p-2 text-right font-semibold text-emerald-700">
                                                {formatTRNumber(editingId === r.id ? ((Number(editQty) || 0) * (Number(editPrice) || 0)) : (r.total_eur || 0), 2)} €
                                            </td>
                                            <td className="p-2 text-center whitespace-nowrap">
                                                {editingId === r.id ? (
                                                    <>
                                                        <Button variant="ghost" size="sm" onClick={() => saveEdit(r.id)} className="text-emerald-700" data-testid={`save-edit-${r.id}`}>
                                                            <Check className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={cancelEdit} className="text-slate-500" data-testid={`cancel-edit-${r.id}`}>
                                                            <XIcon className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button variant="ghost" size="sm" onClick={() => startEdit(r)} className="text-blue-600" data-testid={`edit-${r.id}`}>
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="text-red-600" data-testid={`delete-${r.id}`}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
